import * as React from 'react';

import {
  maybeParseCalendarDate,
  maybeParseZonedDateTime,
} from '@rematter/common/dateTime';
import {
  Button,
  DataGridColumn,
  LocalizedDate,
  LocalizedDateTime,
  Menu,
  maybeEmptyInfo,
} from '@rematter/ui-web';
import {
  LucideArrowRight,
  LucideCheckCheck,
  LucideDownload,
  LucideEllipsis,
  LucidePackageCheck,
  LucidePrinter,
} from 'lucide-react';

import { LoadRecord } from '../../../_graphql/queryLoadList';
import { useTranslations } from '../../../i18n/useTranslations';
import {
  canLoadBeMarkedAsReceived,
  canLoadBeMarkedAsReviewed,
} from '../../../utils/loadUtils';
import { LoadStatusBadge } from '../LoadBadges';

export type UseLoadListColumnsOptions = {
  onRowClick: (row: LoadRecord) => void;
  menuRefs: (id: string) => React.RefObject<HTMLButtonElement | null>;
  setLoadIdForPrint: (id: string | undefined) => void;
  setLoadIdForDownload: (id: string | undefined) => void;
  setLoadIdForReceiving: (id: string | undefined) => void;
  setLoadIdForReviewing: (id: string | undefined) => void;
};

export const useLoadListColumns = ({
  onRowClick,
  menuRefs,
  setLoadIdForPrint,
  setLoadIdForDownload,
  setLoadIdForReceiving,
  setLoadIdForReviewing,
}: UseLoadListColumnsOptions): DataGridColumn<LoadRecord>[] => {
  const t = useTranslations();

  return React.useMemo(
    () => [
      {
        key: 'loadNumber',
        header: t.loadListView.header.loadNumber,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => String(cell.row.loadNumber).padStart(6, '0'),
      },
      {
        key: 'expectedShipDate',
        header: t.loadListView.header.expectedShipDate,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => (
          <LocalizedDate
            date={maybeParseCalendarDate(cell.row.expectedShipDate)}
            dateStyle="medium"
          />
        ),
      },
      {
        key: 'supplier',
        header: t.loadListView.header.supplier,
        width: 240,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => maybeEmptyInfo(cell.row.supplier?.name),
      },
      {
        key: 'status',
        header: t.loadListView.header.status,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => (
          <LoadStatusBadge
            status={cell.row.status}
            reviewed={cell.row.reviewed}
          />
        ),
      },
      {
        key: 'purchaseOrderNumber',
        header: t.loadListView.header.purchaseOrderNumber,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => maybeEmptyInfo(cell.row.purchaseOrderNumber),
      },
      {
        key: 'bookingNumber',
        header: t.loadListView.header.bookingNumber,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => maybeEmptyInfo(cell.row.booking?.documentNumber),
      },
      {
        key: 'containerNumber',
        header: t.loadListView.header.containerNumber,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => maybeEmptyInfo(cell.row.containerNumber),
      },
      {
        key: 'releaseNumber',
        header: t.loadListView.header.releaseNumber,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => maybeEmptyInfo(cell.row.releaseNumber),
      },
      {
        key: 'numberOfMaterials',
        header: t.loadListView.header.numberOfMaterials,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => cell.row.numberOfMaterials,
      },
      {
        key: 'netWeight',
        header: t.loadListView.header.netWeight,
        width: 120,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => cell.row.netWeight,
      },
      {
        key: 'createdAt',
        header: t.loadListView.header.createdAt,
        width: 200,
        onClick: ({ row }) => onRowClick(row),
        cell: (cell) => (
          <LocalizedDateTime
            date={maybeParseZonedDateTime(cell.row.timestamps.createdAt)}
          />
        ),
      },
      {
        key: 'actions',
        header: t.loadListView.header.actions,
        width: 80,
        cell: (cell) => (
          <Menu
            placement="bottom end"
            menuClassName="min-w-30"
            trigger={
              <Button
                ref={menuRefs(cell.row.id)}
                variant={'outline'}
                color="neutral"
              >
                <LucideEllipsis size={16} />
              </Button>
            }
            items={[
              {
                label: 'Open Details',
                icon: <LucideArrowRight />,
                onAction: () => onRowClick(cell.row),
              },
              'separator',
              {
                label: 'Print Documents',
                icon: <LucidePrinter />,
                onAction: () => setLoadIdForPrint(cell.row.id),
              },
              {
                label: 'Download Documents',
                icon: <LucideDownload />,
                onAction: () => setLoadIdForDownload(cell.row.id),
              },
              'separator',
              {
                label: 'Mark as Received',
                icon: <LucidePackageCheck />,
                onAction: () => setLoadIdForReceiving(cell.row.id),
                isDisabled: !canLoadBeMarkedAsReceived(cell.row),
              },
              {
                label: 'Mark as Reviewed',
                icon: <LucideCheckCheck />,
                onAction: () => setLoadIdForReviewing(cell.row.id),
                isDisabled: !canLoadBeMarkedAsReviewed(cell.row),
              },
            ]}
          />
        ),
        pinned: 'right',
      },
    ],
    [
      t,
      onRowClick,
      menuRefs,
      setLoadIdForPrint,
      setLoadIdForDownload,
      setLoadIdForReceiving,
      setLoadIdForReviewing,
    ],
  );
};
